import { type FC, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Bell, Info, XCircle } from 'lucide-react';
import { listAlerts, markAlertsRead, type AlertItem } from '../../api/client.js';
import { cn } from '../../lib/utils.js';
import { WebSocketContext } from '../../hooks/WebSocketContext.js';
import { WebSocketEvent } from '@laxmi-ai/shared';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../ui/dropdown-menu.js';

export const ALERTS_QUERY_KEY = ['alerts'] as const;

function SeverityIcon({ severity }: { severity: AlertItem['severity'] }) {
  const Icon = severity === 'error' ? XCircle : severity === 'warning' ? AlertTriangle : Info;
  return (
    <div
      className={cn(
        'flex h-6 w-6 items-center justify-center rounded-full',
        severity === 'error' && 'bg-red-900/50 text-red-400',
        severity === 'warning' && 'bg-amber-900/50 text-amber-400',
        severity === 'info' && 'bg-blue-900/50 text-blue-400',
      )}
    >
      <Icon className="h-3 w-3" />
    </div>
  );
}

function when(iso: string): string {
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : d.toLocaleString();
}

/**
 * Header bell: the signed-in user's operational alerts (a post failed,
 * several posts failed, a Connector went offline), from GET /v1/notifications,
 * refreshed every minute. It used to read a store that nothing ever wrote to,
 * so it could never show anything. Opening an alert marks it read and goes to
 * the page it is about.
 */
export const AlertBell: FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ALERTS_QUERY_KEY,
    queryFn: () => listAlerts(20),
    refetchInterval: 60_000,
    staleTime: 30_000,
    retry: false,
  });
  // A new alert refreshes the bell at once: the server pushes a signal with
  // no content over the dashboard socket and the bell refetches. The poll
  // above stays as the fallback (socket down, or no socket in tests).
  const socket = useContext(WebSocketContext);
  useEffect(() => {
    if (socket === null) return undefined;
    const refresh = () => {
      void queryClient.invalidateQueries({ queryKey: ALERTS_QUERY_KEY });
    };
    socket.subscribe(WebSocketEvent.ALERTS_CHANGED, refresh);
    return () => socket.unsubscribe(WebSocketEvent.ALERTS_CHANGED, refresh);
  }, [socket, queryClient]);

  const markRead = useMutation({
    mutationFn: (ids?: string[]) => markAlertsRead(ids),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ALERTS_QUERY_KEY });
    },
  });

  const alerts = data?.alerts ?? [];
  const unread = data?.unread ?? 0;

  const openAlert = (alert: AlertItem): void => {
    if (!alert.read) markRead.mutate([alert.id]);
    setOpen(false);
    if (alert.link !== null) navigate(alert.link);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={unread > 0 ? `Alerts, ${unread} unread` : 'Alerts'}
          className="relative flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-800 hover:text-slate-300"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute right-1.5 top-1.5 flex h-4 min-w-[14px] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold text-white">
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Alerts</span>
          {unread > 0 && (
            <button
              type="button"
              onClick={() => markRead.mutate(undefined)}
              className="text-[10px] text-blue-400 hover:text-blue-300"
            >
              Mark all read
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {alerts.length === 0 ? (
          <DropdownMenuItem onSelect={() => navigate('/activity')} className="text-xs text-slate-400">
            No alerts. Failed posts and offline Connectors show up here. Open Activity for recent events.
          </DropdownMenuItem>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {alerts.map((alert) => (
              <DropdownMenuItem
                key={alert.id}
                onSelect={() => openAlert(alert)}
                className={cn('items-start gap-3 py-2.5', !alert.read && 'bg-blue-900/10')}
              >
                <div className="mt-0.5">
                  <SeverityIcon severity={alert.severity} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-slate-200">{alert.title}</p>
                  {alert.description !== null && (
                    <p className="mt-0.5 text-[11px] text-slate-500 break-words">{alert.description}</p>
                  )}
                  <p className="mt-0.5 text-[10px] text-slate-600">{when(alert.createdAt)}</p>
                </div>
              </DropdownMenuItem>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
